import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from ".";

// =============================================================================
// Types
// =============================================================================

type TimezoneMode = "UTC" | "local";

interface TimezoneState {
  value: TimezoneMode;
}

// =============================================================================
// Helpers
// =============================================================================

const STORAGE_KEY = "timezone";

/**
 * Get the browser's local timezone using the Intl API
 */
export const getLocalTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Convert old timezone format to new mode
 * - "Etc/UTC" or "UTC" -> "UTC"
 * - Any local timezone name or "local" -> "local"
 */
const normalizeToMode = (value: string): TimezoneMode => {
  if (value === "UTC" || value === "Etc/UTC") return "UTC";
  if (value === "local") return "local";
  // If it's a timezone name that matches local, treat as local
  // Otherwise treat any non-UTC value as local
  return "local";
};

/**
 * Load initial value from localStorage, normalizing old format
 */
const loadInitialValue = (): TimezoneMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return "local"; // Default to local

  // Handle old format (full timezone names like "America/New_York" or "Etc/UTC")
  if (stored === "Etc/UTC" || stored === "UTC") return "UTC";

  // Any other stored value (old local timezone name) becomes "local"
  return "local";
};

// =============================================================================
// Slice
// =============================================================================

const initialState: TimezoneState = {
  value: loadInitialValue(),
};

export const timezoneSlice = createSlice({
  name: "timezone",
  initialState,
  reducers: {
    /**
     * Initialize timezone - called on app start
     * No longer needed to call moment.tz.setDefault
     */
    initializeTimezone(state) {
      // Re-read from localStorage in case it changed
      state.value = loadInitialValue();
      // Persist normalized value back
      localStorage.setItem(STORAGE_KEY, state.value);
    },

    /**
     * Set timezone value
     * Accepts both old format ("Etc/UTC", timezone names) and new format ("UTC", "local")
     */
    setValue: (state, action: PayloadAction<string>) => {
      state.value = normalizeToMode(action.payload);
      localStorage.setItem(STORAGE_KEY, state.value);
    },

    /**
     * Reset to default (local)
     */
    resetValue: (state) => {
      state.value = "local";
      localStorage.setItem(STORAGE_KEY, state.value);
    },

    /**
     * Set temporary override - kept for backward compatibility
     * Previously used to set moment.tz.setDefault, now a no-op
     * @deprecated No longer needed since we pass timezone explicitly
     */
    setOverride(_state, _action: PayloadAction<string>) {
      // No-op: Override mechanism was only for moment.tz.setDefault
      // Timezone is now passed explicitly to formatting functions
    },

    /**
     * Clear temporary override - kept for backward compatibility
     * @deprecated No longer needed since we pass timezone explicitly
     */
    clearOverride(_state) {
      // No-op: Override mechanism was only for moment.tz.setDefault
    },
  },
});

export const { initializeTimezone, setValue, resetValue, setOverride, clearOverride } = timezoneSlice.actions;

export const timezoneReducer = timezoneSlice.reducer;

// =============================================================================
// Selectors
// =============================================================================

/**
 * Get the current timezone mode ("UTC" or "local")
 */
export const selectTimezoneMode = (state: RootState): TimezoneMode => state.timezone.value;

/**
 * Get the actual timezone string for use with Intl.DateTimeFormat
 * Returns "UTC" or the browser's local timezone (e.g., "America/New_York")
 */
export const selectValue = (state: RootState): string =>
  state.timezone.value === "UTC" ? "UTC" : getLocalTimezone();

/**
 * Get the default (local) timezone name
 * Used for display in timezone selector
 */
export const selectDefaultValue = (state: RootState): string => getLocalTimezone();

/**
 * Check if local timezone is selected
 */
export const selectIsLocal = (state: RootState): boolean => state.timezone.value === "local";

/**
 * Check if UTC timezone is selected
 */
export const selectIsUTC = (state: RootState): boolean => state.timezone.value === "UTC";

/**
 * Get display label for current timezone
 */
export const selectLabel = (state: RootState): string =>
  state.timezone.value === "UTC" ? "UTC" : "Local";
